/**
 * §P.6 fase 2 · Abrir una conversación con la familia de un alumno.
 *
 * ## Se abre desde el alumno, y no desde una lista de personas
 *
 * Los destinatarios se comprueban **contra la base**: tienen que ser tutores de este
 * club, y del alumno cuando el hilo va de un alumno. El contrato dice qué pasaría sin
 * esa comprobación —«sería la forma más fácil de escribirle a cualquier persona con
 * cuenta en Kiap tecleando su `user_id`»— y la interfaz sigue la misma forma: se
 * eligen los tutores **de este alumno**, no se teclea un destinatario.
 *
 * ## Solo a quien ya usa la app
 *
 * Un tutor invitado y sin aceptar no tiene dónde leer el mensaje. Se enseña, se dice
 * por qué no se le puede escribir todavía, y no se ofrece: mandar un mensaje a una
 * cuenta que nadie ha abierto es la forma más silenciosa de no comunicar nada.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Error as PanelError } from '@/shared/ui/Estados';
import { nombreDeTutor, type Tutor } from '@/club/alumnos/tipos';

/** Un tutor solo puede leer si aceptó la invitación. */
const puedeLeer = (t: Tutor): boolean => t.membership_status === 'active';

export function AbrirConversacion({
  club,
  alumnoId,
  nombreDelAlumno,
  tutores,
  alAbrir,
  alCancelar,
}: {
  club: string;
  alumnoId: string;
  nombreDelAlumno: string;
  tutores: readonly Tutor[];
  /** Lleva al chat, que es donde vive la conversación recién abierta. */
  alAbrir: () => void;
  alCancelar: () => void;
}) {
  const cache = useQueryClient();
  const conCuenta = tutores.filter(puedeLeer);
  const sinCuenta = tutores.filter((t) => !puedeLeer(t));

  const [elegidos, setElegidos] = useState<ReadonlySet<string>>(
    // El principal marcado de entrada: es a quien se escribe casi siempre.
    () => new Set(conCuenta.filter((t) => t.is_primary).map((t) => t.user_id))
  );
  const [asunto, setAsunto] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [problema, setProblema] = useState<Problema | null>(null);

  const abrir = useMutation({
    mutationFn: async () => {
      const r = await api.v1.clubs({ club }).conversations.post({
        guardian_user_ids: [...elegidos],
        student_id: alumnoId,
        subject: asunto.trim() === '' ? null : asunto.trim(),
        first_message: mensaje.trim() === '' ? null : mensaje.trim(),
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['conversaciones', club] });
      alAbrir();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const alternar = (id: string) =>
    setElegidos((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <form
      className="formulario"
      onSubmit={(e) => {
        e.preventDefault();
        setProblema(null);
        abrir.mutate();
      }}
    >
      <h2 className="seccion">Escribir a la familia de {nombreDelAlumno}</h2>

      {conCuenta.length === 0 ? (
        <p className="tenue">
          Ningún tutor de {nombreDelAlumno} tiene la app abierta todavía, así
          que no hay dónde mandarle el mensaje. Invítale o reenvíale la
          invitación desde arriba, y cuando entre se le podrá escribir.
        </p>
      ) : (
        <>
          <ul className="roles">
            {conCuenta.map((t) => (
              <li key={t.user_id}>
                <label className="opcion">
                  <input
                    type="checkbox"
                    checked={elegidos.has(t.user_id)}
                    onChange={() => alternar(t.user_id)}
                  />
                  <span>
                    <strong>{nombreDeTutor(t)}</strong>
                    <em>
                      {t.relationship ?? 'tutor'}
                      {t.is_primary ? ' · contacto principal' : ''}
                      {t.email ? ` · ${t.email}` : ''}
                    </em>
                  </span>
                </label>
              </li>
            ))}
          </ul>

          {/*
            Los que no pueden leer: se enseñan y se explica, en vez de que alguien se
            pregunte por qué falta la madre en la lista.
          */}
          {sinCuenta.length > 0 && (
            <p className="tenue">
              {sinCuenta.map(nombreDeTutor).join(', ')}{' '}
              {sinCuenta.length === 1 ? 'no aparece' : 'no aparecen'} porque
              todavía no {sinCuenta.length === 1 ? 'ha' : 'han'} abierto la app:
              no tendría dónde leerlo.
            </p>
          )}

          <div className="rejilla">
            <label>
              De qué va
              <input
                maxLength={160}
                placeholder="La cuota de marzo"
                value={asunto}
                onChange={(e) => setAsunto(e.target.value)}
              />
            </label>
          </div>

          <label>
            Primer mensaje
            <textarea
              rows={3}
              maxLength={4000}
              placeholder="Opcional, pero abrir un hilo vacío deja a la familia sin saber para qué"
              value={mensaje}
              onChange={(e) => setMensaje(e.target.value)}
            />
          </label>

          {/* La regla del canal, donde se escribe. */}
          <p className="tenue">
            <strong>Solo texto.</strong> Este canal no admite fotos ni ficheros:
            es una conversación con la familia de un menor y el riesgo no
            compensa.
          </p>

          {problema && <PanelError problema={problema} />}

          <div className="acciones">
            <button
              type="submit"
              disabled={abrir.isPending || elegidos.size === 0}
            >
              {abrir.isPending
                ? 'Abriendo…'
                : elegidos.size === 0
                  ? 'Marca a quién escribes'
                  : 'Abrir la conversación'}
            </button>
            <button
              type="button"
              className="btn-plano"
              disabled={abrir.isPending}
              onClick={alCancelar}
            >
              Cancelar
            </button>
          </div>
        </>
      )}

      {conCuenta.length === 0 && (
        <div className="acciones">
          <button type="button" className="btn-plano" onClick={alCancelar}>
            Volver
          </button>
        </div>
      )}
    </form>
  );
}
